/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Middleware } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import { initAppVisibilityListener } from "./appVisibilitySlice";

const TAG: string = "appVisibilityMiddleware";

/**
 * Initializes the BG/FG visibility subscription at the store level,
 * removing the need for a dedicated React component.
 *
 * Replaces <BackgroundForegroundListener> — subscription now starts on
 * the first Redux action (before any React render) and is independent
 * of React lifecycle. Model layer is untouched.
 *
 * next(action) is called before subscribe to preserve action ordering.
 */
let unsubscribe: (() => void) | null = null;

export const appVisibilityMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);
    if (unsubscribe === null) {
      Log.i(TAG, "Initializing app visibility listener via middleware");
      unsubscribe = initAppVisibilityListener()(store.dispatch);
    }
    return result;
  };

/**
 * Tears down the BG/FG subscription. Call this in tests (afterEach)
 * to prevent ghost subscriptions across store instances.
 */
export const teardownAppVisibilityMiddleware = (): void => {
  if (unsubscribe !== null) {
    Log.i(TAG, "Tearing down app visibility listener");
    unsubscribe();
    unsubscribe = null;
  }
};
